import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../core/reducers';
import { QueryResultsModel, TypesUtilsService } from '../../../../../../../core/_base/crud';
import { TranslateService } from '@ngx-translate/core';
import { PatientInsuranceService } from '../../../../../../../core/lists/_services';
import { MatSelectChange } from '@angular/material/select';
import { PatientInsuranceModel } from '../../../../../../../core/lists/_models/patient-insurance.model';
import {
  PatientInsuranceOnServerCreated,
  PatientInsuranceOnServerUpdated,
} from '../../../../../../../core/lists/_actions/patient-insurance.actions';
import { cloneDeep } from 'lodash-es';

import {
  selectLastCreatedPatientInsuranceId,
  selectPatientInsuranceInStore,
} from '../../../../../../../core/lists';

@Component({
  selector: 'kt-add-insurance-dialog',
  templateUrl: './add-insurance-dialog.component.html',
  styleUrls: ['./add-insurance-dialog.component.scss'],
})
export class AddInsuranceDialogComponent implements OnInit {
  viewLoading: boolean;
  patientInsuranceForm: any;
  allInsurances: any;
  selectedInsurance: any;
  availablePriorities: any = [
    { key: 'Primary', value: 'Primary' },
    { key: 'Secondary', value: 'Secondary' },
    { key: 'Tertiary', value: 'Tertiary' },
    { key: 'Other', value: 'Other' },
  ];
  isCreate: boolean;
  isReadOnly: boolean;
  private patientInsurance: PatientInsuranceModel | any;
  private allPatientInsurances: PatientInsuranceModel[];
  disableActiveCheckbox: boolean;
  hasFormErrors = false;
  hasSubmissionErrors = false;

  constructor(
    public dialogRef: MatDialogRef<AddInsuranceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private translate: TranslateService,
    private patientInsuranceService: PatientInsuranceService,
    private typesUtilsService: TypesUtilsService,
  ) {}

  ngOnInit(): void {
    this.patientInsuranceService.getTenantInsurances().subscribe((res: any) => {
      this.allInsurances = res.data;
      this.selectedInsurance = this.allInsurances.find(
        (insurance) => insurance.id === this.patientInsuranceForm.value.insurance,
      );
    });
    this.isCreate = this.data.isCreate;
    this.isReadOnly = this.data.isReadOnly;
    if (!this.isCreate) {
      this.patientInsurance = cloneDeep(this.data.patientInsurance);
    } else {
      this.patientInsurance = new PatientInsuranceModel();
    }
    this.store
      .pipe(select(selectPatientInsuranceInStore))
      .subscribe((response: QueryResultsModel) => {
        this.allPatientInsurances = response.items;
      });
    this.createForm();
    if (this.isReadOnly) this.patientInsuranceForm.disable();
  }

  createForm() {
    this.patientInsuranceForm = this.fb.group({
      insurance: [this.patientInsurance.insurance?.id, Validators.required],
      insuranceId: [this.patientInsurance.insurance_no, Validators.required],
      groupNumber: [this.patientInsurance.group_no],
      issueDate: [
        this.isCreate || !this.patientInsurance.issue_date
          ? ''
          : new Date(this.patientInsurance.issue_date).toISOString(),
        [Validators.required],
      ],
      expiryDate: [
        this.isCreate || !this.patientInsurance.expire_date
          ? ''
          : new Date(this.patientInsurance.expire_date).toISOString(),
      ],
      personCode: [this.patientInsurance.person_code, [Validators.required]],
      priority: [this.patientInsurance.priority, [Validators.required]],
      isActive: [this.patientInsurance.is_active],
    });
    if (
      this.patientInsurance.id &&
      !this.isReadOnly &&
      this.patientInsurance.priority !== 'Other'
    ) {
      this.patientInsuranceForm.get('isActive').disable();
    }
  }
  onAlertClose() {
    this.hasFormErrors = false;
  }
  getTitle() {
    let result =
      this.translate.instant('PATIENT.INPUT.INSURANCE.EDIT_PATIENT_INSURANCE') +
      '-' +
      this.patientInsurance.insurance_no;
    if (this.isCreate) {
      result = this.translate.instant('PATIENT.INPUT.INSURANCE.NEW_PATIENT_INSURANCE');
      return result;
    }
    if (this.isReadOnly) {
      result =
        this.translate.instant('PATIENT.INPUT.INSURANCE.VIEW_PATIENT_INSURANCE') +
        '-' +
        this.patientInsurance.insurance_no;
      return result;
    }
    return result;
  }

  save() {
    const values = this.patientInsuranceForm.value;
    this.patientInsurance.expire_date = values.expiryDate;
    this.patientInsurance.group_no = values.groupNumber;
    this.patientInsurance.insurance_id = values.insurance;
    this.patientInsurance.insurance_no = values.insuranceId;
    this.patientInsurance.is_active = !!values.isActive;
    this.patientInsurance.issue_date = values.issueDate;
    this.patientInsurance.person_code = values.personCode;
    this.patientInsurance.priority = values.priority;
    this.patientInsurance.insurance = this.selectedInsurance;
    this.hasFormErrors = false;
    this.hasSubmissionErrors = false;
    const controls = this.patientInsuranceForm.controls;
    /** check form */
    if (this.patientInsuranceForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());

      this.hasFormErrors = true;
      return;
    }
    for (const [key, value] of Object.entries(this.patientInsurance)) {
      if (typeof value === 'string') {
        this.patientInsurance[key] = value.trim();
      }
    }
    if (this.isCreate) {
      this.store.dispatch(
        new PatientInsuranceOnServerCreated({
          patientInsurance: this.patientInsurance,
          patientId: this.data.patientId,
        }),
      );
    } else {
      this.store.dispatch(
        new PatientInsuranceOnServerUpdated({
          patientInsurance: this.patientInsurance,
          patientId: this.data.patientId,
        }),
      );
    }
    this.store.pipe(select(selectLastCreatedPatientInsuranceId)).subscribe(() => {
      this.dialogRef.close({
        done: true,
      });
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.patientInsuranceForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  handleInsuranceChange($event: MatSelectChange) {
    this.selectedInsurance = this.allInsurances.find((insurance) => insurance.id === $event.value);
    this.handleIsActiveCheckbox();
  }

  public handleIsActiveCheckbox() {
    if (!this.selectedInsurance) {
      return;
    }
    this.disableActiveCheckbox = !!this.allPatientInsurances.find(
      (insurance: PatientInsuranceModel) => {
        return (
          insurance.is_active &&
          insurance.insurance.id === this.selectedInsurance.id &&
          insurance.priority === this.patientInsuranceForm.value.priority &&
          insurance.priority !== 'Other'
        );
      },
    );
    if (this.disableActiveCheckbox) {
      this.patientInsuranceForm.get('isActive').disable();
      this.patientInsuranceForm.get('isActive').setValue(false);
      return;
    }
    this.patientInsuranceForm.get('isActive').enable();
  }

  getStatusString(): string {
    if (this.patientInsuranceForm.get('isActive').value === true) {
      return this.translate.instant('GLOBAL.ACTIVE');
    }
    return this.translate.instant('GLOBAL.INACTIVE');
  }
}
